<template>
  <div>
    <div class="app-list widget-access-key-list__wrp">
      <div class="pull-right">
        <permission-wrapper
          skip-alert
          page-name="widgetAccessKeys"
          resource-name="widget_access"
          action-name="create">
          <widget-access-key-editor
            mode="new"
            :loading="updating"
            :on-add="onUpdate"
            :title="'widget-access-key-list.lbl-add' | globalize"
            :details="defaultDetails"
          />
        </permission-wrapper>
      </div>

      <a-table
        size="small"
        :data-source="list"
        :row-key="record=>record.clientId"
        :pagination="false"
        :loading="isPending"
        :row-class-name="(_record, index) => (index % 2 === 1 ? 'ant-table-striped' : null)"
        :columns="columns">
        <template
          slot="clientId-column"
          slot-scope="clientId">
          <copyable :copy-text="clientId">
            {{ clientId }}
          </copyable>
        </template>

        <template
          slot="domain-column"
          slot-scope="domain">
          <copyable :copy-text="domain">
            {{ domain }}
          </copyable>
        </template>

        <template
          slot="key-column"
          slot-scope="key">
          <copyable :copy-text="key">
            {{ key }}
          </copyable>
        </template>

        <template
          slot="createdAt-column"
          slot-scope="createdAt">
          {{ createdAt | formatDateDMYT }}
        </template>

        <template
          slot="createdBy-column"
          slot-scope="createdBy">
          <copyable :copy-text="createdBy">
            <a
              :href="`/users/${createdBy}/details`"
              target="_blank">
              {{ createdBy }}
            </a>
          </copyable>
        </template>

        <template
          slot="actions-column"
          slot-scope="domain,item"
        >
          <a-row>
            <permission-wrapper
              skip-alert
              page-name="widgetAccessKeys"
              resource-name="widget_access"
              action-name="update">
              <widget-access-key-editor
                mode="update"
                :on-add="onUpdate"
                :title="'widget-access-key-list.lbl-update' | globalize"
                :details="item"
              />
            </permission-wrapper>

            <permission-wrapper
              skip-alert
              page-name="widgetAccessKeys"
              resource-name="widget_access"
              action-name="delete">
              <delete-widget-access-key :on-click="() => onRemove(item)" />
            </permission-wrapper>
          </a-row>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
// import Loader from '@/vue/common/Loader'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { api } from '@/api'
import Copyable from '@/vue/common/copyable/Copyable'
import PermissionWrapper from '@/vue/common/permission-wrapper'
import WidgetAccessKeyEditor from '@/vue/pages/widget-access-key/components/editor'
import ATable from 'ant-design-vue/lib/table'
import ARow from 'ant-design-vue/lib/row'
import { globalize } from '@/vue/filters/globalize'
import { Bus } from '@/js/helpers/event-bus'
import DeleteWidgetAccessKey from '@/vue/pages/widget-access-key/components/delete'

const columns = ctx => {
  /* eslint-disable-next-line */
  const h = ctx.$createElement
  return [
    {
      dataIndex: 'clientId',
      key: 'clientId',
      title: globalize('widget-access-key-list.lbl-client-id'),
      width: 200,
      scopedSlots: { customRender: 'clientId-column' },
    },
    {
      dataIndex: 'domain',
      key: 'domain',
      title: globalize('widget-access-key-list.lbl-domain'),
      scopedSlots: { customRender: 'domain-column' },
    },
    {
      dataIndex: 'key',
      key: 'key',
      title: globalize('widget-access-key-list.lbl-key'),
      scopedSlots: { customRender: 'key-column' },
    },
    {
      dataIndex: 'createdBy',
      key: 'createdBy',
      title: globalize('widget-access-key-list.lbl-created-by'),
      scopedSlots: { customRender: 'createdBy-column' },
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      title: globalize('widget-access-key-list.lbl-created-at'),
      scopedSlots: { customRender: 'createdAt-column' },
    },
    {
      dataIndex: 'actions',
      key: 'actions',
      width: 120,
      title: globalize('widget-access-key-list.lbl-actions'),
      scopedSlots: { customRender: 'actions-column' },
    },
  ]
}

const defaultDetails = {
  domain: '',
  key: '',
  clientId: '',
}

const REQUEST_TYPES = {
  create: 'add_widget_access',
  update: 'update_widget_access',
}

export default {
  name: 'widget-access-key-list',
  components: {
    Copyable,
    PermissionWrapper,
    WidgetAccessKeyEditor,
    ATable,
    DeleteWidgetAccessKey,
    ARow,
  },
  data () {
    return {
      isPending: false,
      isLoaded: false,
      isFailed: false,
      updating: false,
      list: [],
      defaultDetails,
    }
  },
  computed: {
    columns () {
      const result = columns(this)

      return result
    },
  },
  watch: {
    isLoaded: {
      handler (value) {
        if (value && this.isPending) {
          this.reloadList()
          this.isPending = false
        }
      },
    },
  },
  async created () {
    await this.getList()
  },
  methods: {
    async getList () {
      this.isLoaded = false
      this.isFailed = false
      this.isPending = true
      let response = {}
      try {
        response = await api.getWithSignature('/widget/access')
        this.list = response.data
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.isLoaded = true
        this.isPending = false
      }

      return response
    },

    async onRemove (item) {
      this.isPending = true
      this.isFailed = false

      try {
        await api.deleteWithSignature(`/widget/access/${item.clientId}`)

        Bus.success('widget-access-key-details.deleted-success-message')
        await this.getList()
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.isPending = false
      }
    },

    async onUpdate (values, mode) {
      this.updating = true
      const data = {
        data: {
          type: REQUEST_TYPES.create,
          attributes: { ...values },
        },
      }
      data.data.attributes.client_id = values.clientId

      let method = 'postWithSignature'
      let path = '/widget/access'
      if (mode !== 'new') {
        method = 'putWithSignature'
        path = `${path}/${values.clientId}`
        data.data.type = REQUEST_TYPES.update
      }
      let response
      let error
      try {
        let body = JSON.stringify(data)

        response = await api[method](path, body)
        Bus.success('widget-access-key-details.success-message')
        await this.getList()

        Bus.emit('widget-access-key-details-updated')
      } catch (e) {
        this.isFailed = true
        ErrorHandler.process(e)
        error = e
      } finally {
        this.updating = false
        /* eslint-disable-next-line */
        return { response, error }
      }
    },
  },
}
</script>

<style scoped>
  @import '~ant-design-vue/lib/table/style/index.css';
  @import '~ant-design-vue/lib/grid/style/index.css';

  .ant-table-striped {
    background: antiquewhite;
  }

  .ant-row span {
    display: inline-block;
  }
</style>
